import { AsyncPipe, DecimalPipe, NgClass, PercentPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Options } from '@angular-slider/ngx-slider';
import { isEqual } from 'lodash-es';
import { distinctUntilChanged, tap } from 'rxjs/operators';

import { InputMaskComponent } from '@shared/components/input-mask/input-mask.component';
import { SliderComponent } from '@shared/components/slider/slider.component';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { VarDirective } from '@shared/directives/var.directive';
import { MoneyPipe } from '@shared/pipes/money.pipe';
import { decimalDivide } from '@shared/utils';

import { InvestigatorPatientVisits } from '../../../../services/investigator-patient-visits.service';
import {
  InvestigatorForecastForm,
  PatientVisitsValues,
} from '../../../../types/investigator-forecast.types';
import { InvestigatorForecastLinkComponent } from '../../../investigator-forecast-link/investigator-forecast-link.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    VarDirective,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    InvestigatorForecastLinkComponent,
    InputMaskComponent,
    SliderComponent,
    MoneyPipe,
    AsyncPipe,
    DecimalPipe,
    PercentPipe,
  ],
  selector: 'aux-investigator-forecast-overview-patient-visits',
  styles: [
    `
      ::ng-deep .aux-slider .ngx-slider {
        flex-grow: 1;
        width: 30%;
      }
      ::ng-deep .forecast-overview-form input {
        color: black;
      }
    `,
  ],
  templateUrl: './overview-patient-visits.component.html',
})
export class InvestigatorForecastOverviewPatientVisitsComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly investigatorPatientVisits = inject(InvestigatorPatientVisits);

  budgetRoute = `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.INDEX}`;
  editMode$ = this.investigatorPatientVisits.editMode$;
  @Input() form!: FormGroup<InvestigatorForecastForm>;
  investigatorSummaryRoute = `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_SUMMARY}`;

  private sliderRange: Options = {
    ceil: 100,
    floor: 0,
  };

  invoiceablesDistributionOptions: Options = {
    ...this.sliderRange,
    hidePointerLabels: true,
  };
  remainingPatientsToDiscontinuePercOptions: Options = this.sliderRange;

  ngOnInit(): void {
    this.form.get(['patientVisits', 'remainingPatientsToDiscontinueCost'])?.disable();

    this.recalculatePatientForm();
  }

  formatDistributionLabel(percent: number) {
    return decimalDivide(percent, 100);
  }

  // eslint-disable-next-line max-lines-per-function
  recalculatePatientForm() {
    const formGroup = this.form.controls.patientVisits;

    formGroup?.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(isEqual),
        tap(() => {
          const values: PatientVisitsValues = formGroup.getRawValue();

          const formattedTotalDiscontinuedPerc = decimalDivide(values.totalDiscontinuedPerc, 100);

          const forecastedRemainingInvoiceables =
            this.investigatorPatientVisits.getForecastedRemainingInvoiceables(
              decimalDivide(values.invoiceablesPerc, 100),
              values.forecastedRemainingVisitCosts
            );

          const forecastedRemainingInvestigatorCosts =
            this.investigatorPatientVisits.getForecastedRemainingInvestigatorCosts(
              values.forecastedRemainingVisitCosts,
              forecastedRemainingInvoiceables
            );

          // discontinue section
          const patientRemainingToDiscontinueAmount =
            this.investigatorPatientVisits.getPatientRemainingToDiscontinueAmount(
              formattedTotalDiscontinuedPerc,
              values.currentDiscontinuedPerc,
              values.currentEnrolledAmount
            );

          const patientRemainingToDiscontinueAmountLeft =
            this.investigatorPatientVisits.getPatientRemainingToDiscontinueAmountLeft(
              values.currentEnrolledAmount,
              formattedTotalDiscontinuedPerc
            );

          const remainingPatientsToDiscontinueCost =
            this.investigatorPatientVisits.getRemainingPatientsToDiscontinueCost(
              decimalDivide(values.remainingPatientsToDiscontinuePerc, 100),
              values.averageCompletedPatientCost
            );

          const differentPatientCost = this.investigatorPatientVisits.getDiscontinuedDifference(
            values.averageCompletedPatientCost,
            remainingPatientsToDiscontinueCost
          );

          const totalDiscontinued = this.investigatorPatientVisits.getTotalDiscontinued(
            differentPatientCost,
            patientRemainingToDiscontinueAmount
          );

          // Spend to date and remaining section
          const totalForecastedRemainingInvestigatorCosts =
            this.investigatorPatientVisits.getTotalForecastedRemainingInvestigatorCosts(
              forecastedRemainingInvestigatorCosts,
              totalDiscontinued
            );

          const totalForecastedInvestigatorAmountThroughEndOfTrial =
            this.investigatorPatientVisits.getTotalForecastedInvestigatorAmountThroughEndOfTrial(
              totalForecastedRemainingInvestigatorCosts,
              values.totalSpendToDate
            );

          formGroup.patchValue(
            {
              differentPatientCost,
              forecastedRemainingInvestigatorCosts,
              forecastedRemainingInvoiceables,
              patientRemainingToDiscontinueAmount,
              patientRemainingToDiscontinueAmountLeft,
              remainingPatientsToDiscontinueCost,
              totalDiscontinued,
              totalForecastedInvestigatorAmountThroughEndOfTrial,
              totalForecastedRemainingInvestigatorCosts,
            },
            { emitEvent: false }
          );
        })
      )
      .subscribe();
  }
}
